import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ExpandMore, ExpandLess } from "@mui/icons-material";
import DrawerComponent from "../../../components/Drawers/drawers";
import SearchComponent from "../../../components/Search/index";
import ModalComponent from "../../../components/Modals/modal";

import {
  addStoreManager,
  getAllStoreLocations,
  getStoreManagementStats,
} from "../../../redux/StoreManagement/storeManagementActions";

export const AddStoreManagerDrawer = ({ showDrawer, setShowDrawer }) => {
  const dispatch = useDispatch();

  // Redux state
  const {
    addStoreManagerLoading,
    addStoreManagerError,
    allStoreLocations,
    allStoreLocationsLoading,
  } = useSelector((state) => state.storeManagement);

  const [managerData, setManagerData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    storeId: "",
    role: "",
    address: "",
  });
  const [errors, setErrors] = useState({});

  // Fetch store locations only when drawer is opened (not on every state change)
  useEffect(() => {
    if (showDrawer) {
      // reset all values
      setManagerData({
        firstName: "",
        lastName: "",
        email: "",
        phoneNumber: "",
        storeId: "",
        role: "",
        address: "",
      });
      setErrors({});
      setSelectedStore(null);
      dispatch(getAllStoreLocations());
    }
  }, [dispatch, showDrawer]);

  // Store selection state
  const [isToggleSelect, setToggleSelect] = useState(false);
  const [selectedStore, setSelectedStore] = useState(null);
  const [searchValue, setSearchValue] = useState("");
  const [storeInUse, setStoreInUse] = useState(null);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  // Mock stores data - this would typically come from Redux state
  const availableStores = (allStoreLocations || []).filter(
    (store) => store.is_active === true
  );

  // Filter stores based on search
  const filteredStores = availableStores.filter((store) =>
    store.name.toLowerCase().includes(searchValue.toLowerCase())
  );

  const handleInputChange = (field, value) => {
    setManagerData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleStoreSelect = (store) => {
    setSelectedStore(store);
    if (
      store.store_manager_name === "No Manager Assigned" ||
      store.store_manager_name === ""
    ) {
      setStoreInUse(false);

      setManagerData((prev) => ({
        ...prev,
        storeId: store.id,
      }));
      setToggleSelect(false);
      setSearchValue("");

      // Clear store error when selection is made
      if (errors.storeId) {
        setErrors((prev) => ({
          ...prev,
          storeId: "",
        }));
      }
    } else {
      setStoreInUse(true);
      setManagerData((prev) => ({
        ...prev,
        storeId: store.id,
      }));
      setToggleSelect(false);
      setSearchValue("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!managerData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!managerData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!managerData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(managerData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!managerData.phoneNumber.trim()) {
      newErrors.phoneNumber = "Phone number is required";
    } else if (!/^[+]?[0-9\s\-()]{10,}$/.test(managerData.phoneNumber)) {
      newErrors.phoneNumber = "Please enter a valid phone number";
    }

    if (!managerData.storeId) {
      newErrors.storeId = "Please select a store";
    }

    if (!managerData.role) {
      newErrors.role = "Please select a role";
    }

    if (!managerData.address.trim()) {
      newErrors.address = "Address is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const addManager = async () => {
    if (!validateForm()) {
      return;
    }

    // Prepare payload for API
    const payload = {
      first_name: managerData.firstName.trim(),
      last_name: managerData.lastName.trim(),
      phone_number: managerData.phoneNumber.trim(),
      email_address: managerData.email.trim(),
      store_id: parseInt(managerData.storeId),
      role: managerData.role,
    };

    try {
      const result = await dispatch(addStoreManager(payload));

      if (addStoreManager.fulfilled.match(result)) {
        // Reset form and close drawer on success
        setManagerData({
          firstName: "",
          lastName: "",
          email: "",
          phoneNumber: "",
          storeId: "",
          role: "",
          address: "",
        });
        setErrors({});
        setShowDrawer(false);
        setSelectedStore(null);
        setShowConfirmModal(false);
        dispatch(getStoreManagementStats());
      }
    } catch (error) {
      // Error is handled in the action and reducer
      console.error("Error adding store manager:", error);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (storeInUse) {
      setShowConfirmModal(true);
      return;
    } else {
      addManager();
    }
  };

  return (
    <>
      <DrawerComponent
        show={showDrawer}
        showDrawer={setShowDrawer}
        title="Add Store Manager"
      >
        <form
          onSubmit={handleSubmit}
          className="bg-white min-h-full overflow-y-auto mt-6"
        >
          {/* First Name and Last Name Row */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            {/* First Name */}
            <div>
              <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
                First Name
              </label>
              <input
                type="text"
                value={managerData.firstName}
                onChange={(e) => handleInputChange("firstName", e.target.value)}
                className={`outline-none border ${
                  errors.firstName ? "border-[#B92043]" : "border-[#96A397]"
                } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
                placeholder="First Name"
              />
              {errors.firstName && (
                <p className="text-[#B92043] text-xs mt-1">
                  {errors.firstName}
                </p>
              )}
            </div>

            {/* Last Name */}
            <div>
              <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
                Last Name
              </label>
              <input
                type="text"
                value={managerData.lastName}
                onChange={(e) => handleInputChange("lastName", e.target.value)}
                className={`outline-none border ${
                  errors.lastName ? "border-[#B92043]" : "border-[#96A397]"
                } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
                placeholder="Last Name"
              />
              {errors.lastName && (
                <p className="text-[#B92043] text-xs mt-1">{errors.lastName}</p>
              )}
            </div>
          </div>

          {/* Email */}
          <div className="mb-6">
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={managerData.email}
              onChange={(e) => handleInputChange("email", e.target.value)}
              className={`outline-none border ${
                errors.email ? "border-[#B92043]" : "border-[#96A397]"
              } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
              placeholder="manager@example.com"
            />
            {errors.email && (
              <p className="text-[#B92043] text-xs mt-1">{errors.email}</p>
            )}
          </div>

          {/* Phone Number */}
          <div className="mb-6">
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              Phone Number
            </label>
            <input
              type="tel"
              value={managerData.phoneNumber}
              onChange={(e) => handleInputChange("phoneNumber", e.target.value)}
              className={`outline-none border ${
                errors.phoneNumber ? "border-[#B92043]" : "border-[#96A397]"
              } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
              placeholder="+234"
            />
            {errors.phoneNumber && (
              <p className="text-[#B92043] text-xs mt-1">
                {errors.phoneNumber}
              </p>
            )}
          </div>

          {/* Store Assignment */}
          <div className="mb-6">
            <div className="relative">
              <p className="font-medium text-sm leading-[18px] text-[#344335]">
                Assign to Store
              </p>
              <div
                className={`flex items-center cursor-pointer justify-between outline-0 h-[44px] bg-white rounded-[8px] border ${
                  errors.storeId ? "border-[#B92043]" : "border-[#96A397]"
                } mt-[4px] px-[14px] w-full font-semibold text-sm leading-5 ${
                  allStoreLocationsLoading
                    ? "opacity-50 cursor-not-allowed"
                    : ""
                }`}
                onClick={() =>
                  !allStoreLocationsLoading && setToggleSelect(!isToggleSelect)
                }
              >
                {selectedStore ? (
                  <span className="flex gap-1">
                    <span className="font-semibold text-sm text-[#344335]">
                      {selectedStore.name}
                    </span>
                  </span>
                ) : (
                  <span className="text-[#344335]">
                    {allStoreLocationsLoading
                      ? "Loading stores..."
                      : "Select store"}
                  </span>
                )}

                {isToggleSelect ? (
                  <ExpandLess
                    className="text-[#ABB6AC] cursor-pointer"
                    style={{ fontSize: "18px" }}
                  />
                ) : (
                  <ExpandMore
                    className="text-[#ABB6AC] cursor-pointer"
                    style={{ fontSize: "18px" }}
                  />
                )}
              </div>

              {isToggleSelect && (
                <div
                  className="absolute mt-2 border rounded-[8px] p-[8px] w-full bg-white z-20 max-h-[250px] overflow-y-scroll"
                  style={{
                    boxShadow:
                      "0px 16px 32px 0px #00000014, 0px 0px 1px 0px #00000040",
                  }}
                >
                  <div className="mb-2">
                    <SearchComponent
                      placeholder="Search stores..."
                      searchValue={searchValue}
                      handleChange={(e) => setSearchValue(e.target.value)}
                    />
                  </div>

                  {filteredStores.length === 0 ? (
                    <div className="flex justify-center items-center h-[100px]">
                      <p className="text-[#96A397] text-sm">No stores found</p>
                    </div>
                  ) : (
                    filteredStores.map((store, index) => {
                      return (
                        <div
                          onClick={() => handleStoreSelect(store)}
                          key={index}
                          className={`flex items-center gap-3 px-[16px] py-[12px] hover:bg-[#F6F7F6] hover:rounded cursor-pointer ${
                            selectedStore?.id === store.id
                              ? "bg-[#EDF7EE] rounded"
                              : ""
                          }`}
                        >
                          <div className="flex-1">
                            <div className="font-semibold text-sm text-[#3E473F]">
                              {store.name}
                            </div>
                            <div className="text-[#3E473F] text-xs font-medium">
                              {store.address || ""}
                              <span className="text-[#5F6D60] ml-1">
                                {store.store_manager_name || ""}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </div>
            {errors.storeId && (
              <p className="text-[#B92043] text-xs mt-1">{errors.storeId}</p>
            )}
          </div>

          {/* Role Assignment */}
          <div className="mb-6">
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              Role
            </label>
            <div className="relative">
              <select
                value={managerData.role}
                onChange={(e) => handleInputChange("role", e.target.value)}
                className={`outline-none border ${
                  errors.role ? "border-[#B92043]" : "border-[#96A397]"
                } w-full h-[44px] rounded-lg px-3 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5 bg-white appearance-none`}
              >
                <option value="">Select Role</option>
                <option value="ADMIN">Admin</option>
                <option value="MEMBER">Member</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg
                  className="w-4 h-4 text-[#96A397]"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>
            {errors.role && (
              <p className="text-[#B92043] text-xs mt-1">{errors.role}</p>
            )}
          </div>

          {/* Address */}
          <div className="mb-8">
            <label className="block text-[#344335] font-normal text-sm leading-[18px] mb-1">
              Address
            </label>
            <input
              type="text"
              value={managerData.address}
              onChange={(e) => handleInputChange("address", e.target.value)}
              className={`outline-none border ${
                errors.address ? "border-[#B92043]" : "border-[#96A397]"
              } w-full h-[44px] rounded-lg px-3 py-4 placeholder:text-[#96A397] focus:border-[#2B8C34] focus:outline-none focus:shadow-input text-sm font-medium leading-5`}
              placeholder="Manager's residential address"
            />
            {errors.address && (
              <p className="text-[#B92043] text-xs mt-1">{errors.address}</p>
            )}
          </div>

          {/* API Error Display */}
          {addStoreManagerError && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{addStoreManagerError}</p>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={addStoreManagerLoading}
            className="w-full outline-none disabled:bg-[#ABB6AC] disabled:border-[#ABB6AC] cursor-pointer bg-primary border border-primary text-white font-medium text-sm leading-5 py-[10px] px-[20px] rounded-[8px] hover:border hover:border-primary transition ease-in-out delay-100"
          >
            {addStoreManagerLoading
              ? "Adding Store Manager..."
              : "Add Store Manager"}
          </button>
        </form>
      </DrawerComponent>

      <ModalComponent
        show={showConfirmModal}
        showModal={() => setShowConfirmModal(!showConfirmModal)}
        title="Confirm Store Manager"
        subtitle=""
      >
        <div className="flex flex-col gap-4">
          <p className="text-sm text-[#344335]">
            The store you selected already has a store manager. Are you sure you
            want to replace the store manager?
          </p>
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setShowConfirmModal(false)}
              className="bg-[#B92043] 
                    disabled:bg-[#B92043] disabled:bg-opacity-[0.7]
                    rounded-lg px-6 py-3 text-white text-sm font-medium hover:bg-opacity-[0.9] hover:bg-[#B92043] transition-all"
            >
              Cancel
            </button>
            <button
              onClick={addManager}
              className="bg-primary 
                        disabled:bg-[#ABB6AC] 
                        rounded-lg px-6 py-3 text-white text-sm font-medium hover:bg-[#24752B] transition-all"
            >
              Replace Manager
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export default AddStoreManagerDrawer;
